/**
 * i18n — site-wide Spanish → English toggle without touching every page.
 *
 * Strategy
 * ─────────
 * 1. All page JSX stays in Mexican Spanish (the source of truth).
 * 2. When the user flips the toggle to "en":
 *    - We walk the DOM, collect every unique Spanish text node that isn't
 *      inside a translate-skip region.
 *    - We check localStorage first (we persist translations forever).
 *    - Any still-missing strings are sent in ONE batch to the backend
 *      `/api/translate/batch` endpoint, which translates and caches them
 *      in MongoDB so the next visitor gets them for free.
 *    - We replace each text node with the English copy, remembering the
 *      original on a WeakMap so we can restore when toggled back.
 * 3. A MutationObserver re-translates any new nodes that appear after
 *    navigation / lazy loads / modal opens.
 *
 * What we SKIP
 * ────────────
 * Any element with:
 *   - `data-no-translate="true"` (brand marks, logo text, admin UI, chatbot)
 *   - Inside `<script>`, `<style>`, `<code>`, `<pre>`
 *   - A text node that is purely numeric / dates / short symbols
 *   - A text node that is already recognised as English (ASCII only, no es-only chars)
 *     — this heuristic has a safety net: when we send to the backend, duplicates
 *     are deduped and English-looking strings get skipped there too.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "./lib/api";

const I18nContext = createContext({ lang: "es", setLang: () => {}, loading: false });

export const useI18n = () => useContext(I18nContext);

const STORAGE_LANG = "vbi-lang";
const STORAGE_DICT = "vbi-i18n-dict-v1";

const loadDict = () => {
  if (typeof window === "undefined") return {};
  try { return JSON.parse(window.localStorage.getItem(STORAGE_DICT) || "{}"); }
  catch { return {}; }
};
const saveDict = (d) => {
  try { window.localStorage.setItem(STORAGE_DICT, JSON.stringify(d)); } catch {}
};

const isMostlyAscii = (s) => {
  // Letters-only ASCII? Then it's probably already English (or a brand token).
  // We skip strings that have no es-only characters AND no common Spanish words.
  if (/[áéíóúñü¿¡]/i.test(s)) return false;
  // Short strings (<= 2 words, 0 Spanish markers): check if it looks obviously
  // Spanish by common stop-words. If not, skip the backend trip.
  const words = s.trim().toLowerCase().split(/\s+/);
  const SPANISH_WORDS = new Set([
    "el","la","los","las","un","una","unos","unas","de","del","y","o","en",
    "con","sin","para","por","que","qué","es","son","al","su","sus","le","les",
    "lo","nos","no","sí","si","como","cómo","más","muy","desde","hasta","sobre",
    "entre","porque","pero","ya","aún","hoy","mañana","noche","tarde","sin",
    "privado","privada","boletín","boletines","valle","bravo","nosotros","ustedes",
    "nuestro","nuestra","nuestros","nuestras","ellos","ellas","aquí","allá","todo",
    "todos","nada","algo","pueblo","casa","agua","sol","lago","bosque","otoño",
    "invierno","primavera","verano","noviembre","octubre","septiembre","agosto",
    "julio","junio","mayo","abril","marzo","febrero","enero","diciembre",
    "cena","comida","desayuno","almuerzo","chef","vino","mezcal","cerveza",
    "música","concierto","concierto","clase","taller","retiro","cabalgata","vela",
    "triatlón","mercadito","oktoberfest","noche","día","fin","semana","evento",
    "eventos","experiencia","experiencias","hola","gracias","buenos","buenas",
    "ver","leer","abrir","reservar","hablar","decir","contar","contarnos",
    "escribir","escribirnos","confirmar"
  ]);
  const hits = words.filter((w) => SPANISH_WORDS.has(w.replace(/[.,:;!?()"'…·]/g, "")));
  if (hits.length === 0 && words.length <= 2) return true;
  return false;
};

const shouldTranslate = (text) => {
  const t = text.trim();
  if (!t) return false;
  if (t.length < 2) return false;
  // Pure numbers / dates / money / symbols → skip
  if (/^[\d\s.,:/\-+%$€¥@#·°]+$/.test(t)) return false;
  // Single roman numeral or short all-caps token (e.g. "VOL. XI", "N° 11")
  if (/^[A-ZIVXLCDM\s.°º·—–\-]{2,10}$/.test(t) && !/[a-z]/.test(t)) return false;
  return true;
};

const SKIP_TAGS = new Set(["SCRIPT","STYLE","CODE","PRE","NOSCRIPT","TEXTAREA","INPUT","SELECT","OPTION"]);

const isSkipped = (node) => {
  // Walk up ancestors to find data-no-translate / data-i18n="skip"
  let el = node.parentElement;
  while (el) {
    if (SKIP_TAGS.has(el.tagName)) return true;
    if (el.dataset?.noTranslate === "true") return true;
    if (el.getAttribute && el.getAttribute("translate") === "no") return true;
    el = el.parentElement;
  }
  return false;
};

const ORIGINAL = new WeakMap(); // text-node → original Spanish string

const collectTextNodes = (root) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (isSkipped(node)) return NodeFilter.FILTER_REJECT;
      const raw = node.nodeValue || "";
      // if we've already translated this exact node, let it through so we can
      // optionally refresh with the new dict (but we check equality below)
      if (!shouldTranslate(raw)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const out = [];
  let n;
  while ((n = walker.nextNode())) out.push(n);
  return out;
};

const applyDict = (nodes, dict) => {
  for (const node of nodes) {
    const original = ORIGINAL.get(node) ?? node.nodeValue;
    const key = original.trim();
    const translated = dict[key];
    if (translated && translated !== key) {
      if (!ORIGINAL.has(node)) ORIGINAL.set(node, node.nodeValue);
      // preserve leading/trailing whitespace
      const raw = node.nodeValue || "";
      const lead = raw.slice(0, raw.length - raw.trimStart().length);
      const trail = raw.slice(raw.trimEnd().length);
      node.nodeValue = `${lead}${translated}${trail}`;
    }
  }
};

const restoreOriginals = (root) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let n;
  while ((n = walker.nextNode())) {
    const orig = ORIGINAL.get(n);
    if (orig !== undefined && n.nodeValue !== orig) n.nodeValue = orig;
  }
};

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "es";
    return window.localStorage.getItem(STORAGE_LANG) || "es";
  });
  const [loading, setLoading] = useState(false);
  const dictRef = useRef(loadDict());
  const observerRef = useRef(null);
  const prewarmInFlight = useRef(false);

  const translateDom = useCallback(async (root = document.body) => {
    if (!root) return;
    const nodes = collectTextNodes(root);
    if (nodes.length === 0) return;
    // First pass: apply what we have cached
    applyDict(nodes, dictRef.current);
    // Collect strings we still need
    const needed = [];
    for (const n of nodes) {
      const original = ORIGINAL.get(n) ?? n.nodeValue;
      const key = original.trim();
      if (!dictRef.current[key] && key.length > 0) needed.push(key);
    }
    if (needed.length === 0) return;
    const uniq = Array.from(new Set(needed));
    try {
      setLoading(true);
      // Fire batches in PARALLEL — Claude handles 50 strings/call in ~2.5s,
      // so 3 concurrent calls finish ~1 batch of time instead of 3 serial.
      const BATCH = 50;
      const batches = [];
      for (let i = 0; i < uniq.length; i += BATCH) {
        batches.push(uniq.slice(i, i + BATCH));
      }
      const results = await Promise.all(
        batches.map((slice) =>
          api.post("/translate/batch", { strings: slice, lang: "en" })
            .then((r) => (r.data && r.data.translations) || {})
            .catch(() => ({}))
        )
      );
      for (const got of results) {
        dictRef.current = { ...dictRef.current, ...got };
      }
      saveDict(dictRef.current);
      // Final pass across the WHOLE document (user may have navigated)
      applyDict(collectTextNodes(document.body), dictRef.current);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("i18n batch failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Pre-warm the dictionary in the background on first ES page load, so when
   * the user clicks EN the switch is instant (no spinner, no API wait).
   * Runs silently — no visible loading state. Caches in both localStorage
   * and the server-side `translations` collection.
   */
  const prewarm = useCallback(async () => {
    if (typeof window === "undefined") return;
    if (prewarmInFlight.current) return;
    const nodes = collectTextNodes(document.body);
    if (nodes.length === 0) return;
    const needed = [];
    for (const n of nodes) {
      const key = (n.nodeValue || "").trim();
      if (!dictRef.current[key] && key.length > 0) needed.push(key);
    }
    if (needed.length === 0) return;
    const uniq = Array.from(new Set(needed));
    const BATCH = 50;
    const batches = [];
    for (let i = 0; i < uniq.length; i += BATCH) batches.push(uniq.slice(i, i + BATCH));
    try {
      prewarmInFlight.current = true;
      const results = await Promise.all(
        batches.map((slice) =>
          api.post("/translate/batch", { strings: slice, lang: "en" })
            .then((r) => (r.data && r.data.translations) || {})
            .catch(() => ({}))
        )
      );
      for (const got of results) dictRef.current = { ...dictRef.current, ...got };
      saveDict(dictRef.current);
    } finally {
      prewarmInFlight.current = false;
    }
  }, []);

  const setLang = useCallback(
    (next) => {
      if (next === lang) return;
      setLangState(next);
      try { window.localStorage.setItem(STORAGE_LANG, next); } catch {}
      if (next === "es") {
        restoreOriginals(document.body);
      } else {
        translateDom(document.body);
      }
    },
    [lang, translateDom]
  );

  // Boot: if saved pref is EN on first mount, translate the initial DOM.
  // If saved pref is ES, silently pre-warm the dictionary so the EN flip is
  // instant later.
  useEffect(() => {
    const id = setTimeout(() => {
      if (lang === "en") {
        translateDom(document.body);
      } else {
        // Pre-warm in background, non-blocking
        prewarm();
      }
    }, 400);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-warm again whenever the user navigates while in ES mode, so the
  // dictionary is always ready for the pages the user is actually visiting.
  useEffect(() => {
    if (lang !== "es") return;
    const id = setTimeout(() => prewarm(), 800);
    return () => clearTimeout(id);
  }, [lang, prewarm]);

  // Observe DOM for new content. In EN mode we translate it. In ES mode we
  // silently pre-warm the dictionary so a later EN flip is instant.
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    let scheduled = null;
    const flush = () => {
      scheduled = null;
      if (lang === "en") translateDom(document.body);
      else prewarm();
    };
    observerRef.current = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = setTimeout(flush, 400);
    });
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: false,
    });
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [lang, translateDom, prewarm]);

  return (
    <I18nContext.Provider value={{ lang, setLang, loading }}>
      {children}
    </I18nContext.Provider>
  );
}
